/**
 * The one record status vocabulary (owner, 2026-09-21; migration 0037).
 *
 * Farmers, parties, plots and KYC records - and the farmer's KYC and consent summaries - all
 * use these four values. The database pins them with CHECK constraints and guards every move:
 * a record is born Pending, any other status needs the screen's `approve` right, Suspended and
 * Terminated need a reason, and only an organisation admin brings back a Terminated record.
 */
export const RECORD_STATUSES = ['pending', 'verified', 'suspended', 'terminated'] as const;
/**
 * Farmers and plots can also be Withdrawn (migration 0066) - never set by hand, only when the
 * farmer signs a withdrawal consent from the withdrawal register.
 */
export const ALL_RECORD_STATUSES = [...RECORD_STATUSES, 'withdrawn'] as const;
export type RecordStatus = (typeof ALL_RECORD_STATUSES)[number];

export const RECORD_STATUS_LABELS: Record<RecordStatus, string> = {
  pending: 'Pending',
  verified: 'Verified',
  suspended: 'Suspended',
  terminated: 'Terminated',
  withdrawn: 'Withdrawn',
};

/** Which entity `api.fn_set_record_status` moves. */
export type StatusEntity = 'farmer' | 'plot' | 'party' | 'kyc' | 'contractor' | 'worker';

export function needsReason(status: string): boolean {
  return status === 'suspended' || status === 'terminated';
}

/**
 * Stat cards for the four statuses. A screen spreads these after its own "All" card, so every
 * register filters by status the same way.
 */
export function statusStats<T>(statusOf: (row: T) => string | null | undefined) {
  const tone: Record<RecordStatus, 'warn' | 'ok' | 'info' | 'error'> = {
    pending: 'warn', verified: 'ok', suspended: 'info', terminated: 'error', withdrawn: 'info',
  };
  return ALL_RECORD_STATUSES.map((status) => ({
    key: status,
    label: RECORD_STATUS_LABELS[status],
    tone: tone[status],
    match: (row: T) => statusOf(row) === status,
  }));
}
